if (!customElements.get('kt-animated-story')) {
  class KTAnimatedStory extends HTMLElement {
    connectedCallback() {
      if (this.isReady) return;

      this.isReady = true;
      this.nav = this.querySelector('[data-kt-story-nav]');
      this.panel = this.querySelector('[data-kt-story-panel]');
      this.tabs = Array.from(this.querySelectorAll('[data-kt-story-tab]'));
      this.templates = Array.from(this.querySelectorAll('template[data-kt-story-template]'));
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.activeIndex = 0;

      this.handleClick = (event) => {
        const tab = event.target.closest('[data-kt-story-tab]');
        if (!tab || !this.contains(tab)) return;
        this.activate(Number(tab.dataset.ktStoryIndex), { replay: true, scroll: true });
      };
      this.handleKeydown = (event) => this.onKeydown(event);
      this.handleScroll = () => this.onNavScroll();
      this.handlePointerDown = (event) => this.onPointerDown(event);
      this.handlePointerMove = (event) => this.onPointerMove(event);
      this.handlePointerUp = (event) => this.onPointerUp(event);
      this.handleBlockSelect = (event) => {
        const index = this.tabs.findIndex((tab) => tab.dataset.blockId === event.detail?.blockId);
        if (index >= 0) this.activate(index, { replay: false, scroll: true });
      };

      this.nav?.addEventListener('click', this.handleClick);
      this.nav?.addEventListener('keydown', this.handleKeydown);
      this.nav?.addEventListener('scroll', this.handleScroll, { passive: true });
      this.panel?.addEventListener('pointerdown', this.handlePointerDown);
      this.panel?.addEventListener('pointermove', this.handlePointerMove, { passive: false });
      this.panel?.addEventListener('pointerup', this.handlePointerUp);
      this.panel?.addEventListener('pointercancel', this.handlePointerUp);
      this.addEventListener('shopify:block:select', this.handleBlockSelect);
      this.dataset.ready = 'true';
      this.syncTabs(0);
    }

    disconnectedCallback() {
      this.nav?.removeEventListener('click', this.handleClick);
      this.nav?.removeEventListener('keydown', this.handleKeydown);
      this.nav?.removeEventListener('scroll', this.handleScroll);
      this.panel?.removeEventListener('pointerdown', this.handlePointerDown);
      this.panel?.removeEventListener('pointermove', this.handlePointerMove);
      this.panel?.removeEventListener('pointerup', this.handlePointerUp);
      this.panel?.removeEventListener('pointercancel', this.handlePointerUp);
      this.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.clearTimeout(this.scrollTimer);
      window.clearTimeout(this.swapTimer);
      this.isReady = false;
    }

    onPointerDown(event) {
      if (!this.mobileQuery.matches || !event.isPrimary) return;
      if (event.target.closest('a, input, select, textarea, button:not([data-kt-lottie-trigger])')) return;
      this.pointerId = event.pointerId;
      this.pointerStartX = event.clientX;
      this.pointerStartY = event.clientY;
      this.pointerLastX = event.clientX;
      this.pointerLastY = event.clientY;
      this.horizontalGesture = false;
      try {
        this.panel?.setPointerCapture?.(event.pointerId);
      } catch {
        // Synthetic touch tests do not create a native active pointer.
      }
    }

    onPointerMove(event) {
      if (event.pointerId !== this.pointerId) return;
      this.pointerLastX = event.clientX;
      this.pointerLastY = event.clientY;
      const deltaX = event.clientX - this.pointerStartX;
      const deltaY = event.clientY - this.pointerStartY;

      if (!this.horizontalGesture && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
        this.horizontalGesture = true;
      }
      if (this.horizontalGesture) event.preventDefault();
    }

    onPointerUp(event) {
      if (event.pointerId !== this.pointerId) return;
      const deltaX = (this.pointerLastX ?? event.clientX) - this.pointerStartX;
      const deltaY = (this.pointerLastY ?? event.clientY) - this.pointerStartY;
      const threshold = Math.max(42, (this.panel?.clientWidth || 300) * 0.14);

      if (this.horizontalGesture && Math.abs(deltaX) >= threshold && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
        const direction = deltaX < 0 ? 1 : -1;
        const nextIndex = Math.min(this.tabs.length - 1, Math.max(0, this.activeIndex + direction));
        if (nextIndex !== this.activeIndex) this.activate(nextIndex, { replay: true, scroll: true });
      }

      this.pointerId = null;
      this.horizontalGesture = false;
      try {
        if (this.panel?.hasPointerCapture?.(event.pointerId)) this.panel.releasePointerCapture(event.pointerId);
      } catch {
        // The pointer may already have been released by the browser.
      }
    }

    onKeydown(event) {
      const current = this.tabs.indexOf(event.target.closest('[data-kt-story-tab]'));
      if (current < 0) return;

      let next = current;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (current + 1) % this.tabs.length;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (current - 1 + this.tabs.length) % this.tabs.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.tabs.length - 1;
      else return;

      event.preventDefault();
      this.tabs[next].focus();
      this.activate(next, { replay: true, scroll: true });
    }

    onNavScroll() {
      if (!this.mobileQuery.matches || this.isProgrammaticScroll) return;
      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(() => {
        const maximumScroll = this.nav.scrollWidth - this.nav.clientWidth;
        if (this.nav.scrollLeft <= 4) {
          if (this.activeIndex !== 0) this.activate(0, { replay: true, scroll: false });
          return;
        }
        if (this.nav.scrollLeft >= maximumScroll - 4) {
          const lastIndex = this.tabs.length - 1;
          if (this.activeIndex !== lastIndex) this.activate(lastIndex, { replay: true, scroll: false });
          return;
        }

        const navCenter = this.nav.getBoundingClientRect().left + this.nav.clientWidth / 2;
        let closestIndex = this.activeIndex;
        let closestDistance = Number.POSITIVE_INFINITY;

        this.tabs.forEach((tab, index) => {
          const rect = tab.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - navCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        if (closestIndex !== this.activeIndex) this.activate(closestIndex, { replay: true, scroll: false });
      }, 120);
    }

    activate(index, { replay = true, scroll = false } = {}) {
      if (!Number.isInteger(index) || index < 0 || index >= this.tabs.length) return;

      if (index === this.activeIndex) {
        if (replay) this.panel?.querySelector('kt-lottie-player')?.requestPlay?.();
        if (scroll) this.centerTab(index);
        return;
      }

      const template = this.templates[index];
      if (!template || !this.panel) return;

      this.activeIndex = index;
      this.syncTabs(index);
      this.panel.classList.add('is-changing');
      window.clearTimeout(this.swapTimer);

      const swap = () => {
        this.panel.replaceChildren(template.content.cloneNode(true));
        requestAnimationFrame(() => {
          this.panel.classList.remove('is-changing');
          if (replay) this.panel.querySelector('kt-lottie-player')?.requestPlay?.();
        });
      };

      if (this.motionQuery.matches) swap();
      else this.swapTimer = window.setTimeout(swap, 90);
      if (scroll) this.centerTab(index);
    }

    syncTabs(index) {
      this.tabs.forEach((tab, tabIndex) => {
        const isActive = tabIndex === index;
        tab.setAttribute('aria-selected', String(isActive));
        tab.tabIndex = isActive ? 0 : -1;
      });
      if (this.panel) this.panel.setAttribute('aria-labelledby', this.tabs[index]?.id || '');
    }

    centerTab(index) {
      if (!this.mobileQuery.matches || !this.nav) return;
      const tab = this.tabs[index];
      if (!tab) return;

      const target = tab.offsetLeft - (this.nav.clientWidth - tab.offsetWidth) / 2;
      this.isProgrammaticScroll = true;
      this.nav.scrollTo({ left: target, behavior: this.motionQuery.matches ? 'auto' : 'smooth' });
      window.setTimeout(() => {
        this.isProgrammaticScroll = false;
      }, this.motionQuery.matches ? 0 : 260);
    }
  }

  customElements.define('kt-animated-story', KTAnimatedStory);
}
